/** Low-Level Design interview prep content — principles, patterns, design problems. */

export const lldContent = {
  solid: {
    title: 'SOLID Principles',
    subtitle: 'Five rules that keep object-oriented designs flexible as requirements change.',
    readingMinutes: 10,
    blocks: [
      { type: 'p', text: 'SOLID is a checklist senior interviewers use to judge whether your class design will survive real change. You do not need to name-drop every letter — but your code should clearly follow the spirit of each principle.' },
      { type: 'diagram', kind: 'solid-principles' },
      { type: 'h', text: 'The five principles in plain English' },
      { type: 'table', headers: ['Letter', 'Principle', 'One-line meaning'], rows: [
        ['S', 'Single Responsibility', 'One class → one reason to change'],
        ['O', 'Open/Closed', 'Extend behavior without editing old code'],
        ['L', 'Liskov Substitution', 'Subtypes must honor the parent contract'],
        ['I', 'Interface Segregation', 'Small interfaces beat one fat interface'],
        ['D', 'Dependency Inversion', 'Depend on abstractions, not concrete classes'],
      ]},
      { type: 'p', text: 'Single Responsibility (SRP) means a class should do one job. If `InvoiceService` both calculates tax and sends emails, a tax-rule change forces you to touch email code. Split responsibilities so each class has a clear owner.' },
      { type: 'code', language: 'java', code: `// Bad: one class, two reasons to change
class OrderProcessor {
  void process(Order o) { validate(o); charge(o); sendEmail(o); }
}

// Better: separate concerns
class OrderValidator { void validate(Order o) { /* ... */ } }
class PaymentService { void charge(Order o) { /* ... */ } }
class NotificationService { void sendEmail(Order o) { /* ... */ } }` },
      { type: 'p', text: 'Open/Closed (OCP): add new discount types by introducing a new class, not by adding another `if` in a giant method. Strategy or Factory patterns are common OCP tools.' },
      { type: 'p', text: 'Liskov Substitution (LSP): if code expects a `Bird`, a `Penguin` must not break callers by throwing "cannot fly" where fly was promised. Favor composition when inheritance creates awkward overrides.' },
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'When asked "apply SOLID to parking lot," pick one class (e.g. `ParkingSpot`) and explain which principle you are protecting. Interviewers prefer one deep example over listing all five vaguely.' },
      { type: 'callout', tone: 'tip', title: 'When not to over-apply', text: 'Do not create five interfaces for a 50-line homework problem. Mention trade-offs: SOLID reduces coupling but adds indirection. Start simple; refactor toward SOLID when change appears.' },
    ],
    references: [
      { label: 'SOLID — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/what-is-pattern' },
      { label: 'SOLID Principles — Oracle Java Tutorials (OOP concepts)', url: 'https://docs.oracle.com/javase/tutorial/java/concepts/' },
      { label: 'SOLID — GeeksforGeeks overview', url: 'https://www.geeksforgeeks.org/s-solid-principles-explained-in-detail/' },
    ],
  },

  'oop-fundamentals': {
    title: 'OOP Fundamentals',
    subtitle: 'Classes, objects, and the four pillars every LLD answer builds on.',
    readingMinutes: 9,
    blocks: [
      { type: 'p', text: 'Object-Oriented Programming models software as objects that combine data (fields) and behavior (methods). In interviews you translate nouns from requirements into classes and verbs into methods.' },
      { type: 'h', text: 'The four pillars' },
      { type: 'ul', items: [
        'Encapsulation — hide internal state; expose safe operations',
        'Abstraction — show what matters, hide implementation detail',
        'Inheritance — reuse and specialize via "is-a" relationships',
        'Polymorphism — one interface, many implementations at runtime',
      ]},
      { type: 'p', text: 'A `BankAccount` encapsulates balance: callers use `deposit()` and `withdraw()` instead of mutating balance directly. That guardrail prevents invalid states like negative balances without permission.' },
      { type: 'code', language: 'java', code: `abstract class PaymentMethod {
  abstract boolean pay(Money amount);
}

class CreditCard extends PaymentMethod {
  boolean pay(Money amount) { /* charge card */ return true; }
}

class UPI extends PaymentMethod {
  boolean pay(Money amount) { /* UPI flow */ return true; }
}

// Polymorphism: caller depends on PaymentMethod, not each concrete type
void checkout(PaymentMethod method, Money total) {
  method.pay(total);
}` },
      { type: 'h', text: 'How to go from requirements to classes' },
      { type: 'steps', steps: [
        { title: 'Find nouns', text: 'User, Ticket, Seat, Elevator — each strong noun is a candidate class.' },
        { title: 'Find verbs', text: 'book, cancel, move, dispatch — these become methods on the right owner.' },
        { title: 'Find relationships', text: 'A Booking has many Seats; an Elevator serves a Floor — draw associations early.' },
        { title: 'Identify enums', text: 'Status, Role, PieceColor — use enums instead of magic strings.' },
      ]},
      { type: 'diagram', kind: 'class-diagram' },
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Say out loud: "I will start with core entities, then add services for orchestration." Separating entity classes from service classes is a signal you have built real systems.' },
      { type: 'callout', tone: 'warn', title: 'Common mistake', text: 'Creating a class for every tiny concept (e.g. `ButtonColor`) or one God class that does everything. Aim for cohesive classes with clear names.' },
    ],
    references: [
      { label: 'What Is OOP? — Oracle Java Tutorial', url: 'https://docs.oracle.com/javase/tutorial/java/concepts/' },
      { label: 'Composition vs Inheritance — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/composite' },
    ],
  },

  uml: {
    title: 'UML for LLD Interviews',
    subtitle: 'Lightweight class and sequence diagrams that clarify your design fast.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'You rarely need full UML notation in a 45-minute LLD round. Interviewers want a clear class diagram: class names, key fields, important methods, and relationships (inheritance, association, aggregation).' },
      { type: 'h', text: 'Symbols that matter in interviews' },
      { type: 'table', headers: ['Notation', 'Meaning', 'Example'], rows: [
        ['A ──▶ B', 'Association / uses', 'OrderService uses PaymentGateway'],
        ['A ◆── B', 'Composition (strong ownership)', 'House owns Rooms; room dies with house'],
        ['A ◇── B', 'Aggregation (weaker ownership)', 'Department has Employees'],
        ['A ──▷ B', 'Inheritance', 'CreditCardPayment extends Payment'],
        ['1..*', 'Multiplicity', 'Customer has 1..* Orders'],
      ]},
      { type: 'p', text: 'Sequence diagrams help when flow is tricky — e.g. BookMyShow seat lock then payment. Draw actors left-to-right and message arrows for `lockSeat()`, `pay()`, `confirmBooking()`.' },
      { type: 'diagram', kind: 'class-diagram' },
      { type: 'h', text: 'Minimal class box template' },
      { type: 'code', language: 'text', code: `┌─────────────────────┐
│     ParkingLot      │
├─────────────────────┤
│ - spots: Spot[]     │
│ - levels: Level[]   │
├─────────────────────┤
│ + parkVehicle(v)    │
│ + unparkVehicle(t)  │
│ + findSpot(v): Spot │
└─────────────────────┘` },
      { type: 'ol', items: [
        'List 5–8 core classes first — not every helper.',
        'Mark interfaces with «interface» or a different color.',
        'Add methods you will actually implement in code.',
        'Update the diagram when requirements change (extensibility follow-up).',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Draw while you talk. If you run out of time, a clean class diagram beats 200 lines of half-written code. Ask: "Should I detail the payment flow or the data model?"' },
      { type: 'callout', tone: 'tip', title: 'Tools', text: 'Excalidraw, draw.io, or a whiteboard all work. Consistency matters more than perfect UML syntax.' },
    ],
    references: [
      { label: 'UML Class Diagrams — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/what-is-pattern' },
      { label: 'UML Basics — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/unified-modeling-language-uml-class-diagrams/' },
    ],
  },

  'composition-over-inheritance': {
    title: 'Composition over Inheritance',
    subtitle: 'Build behavior by combining objects instead of deep inheritance trees.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Inheritance shares code quickly but couples subclasses to parent implementation. Composition means an object holds references to other objects and delegates work — easier to swap behaviors at runtime.' },
      { type: 'h', text: 'Problem with deep inheritance' },
      { type: 'code', language: 'java', code: `// Fragile hierarchy: every new combo needs a new subclass
class Bird { void fly() {} }
class FlyingDuck extends Bird {}
class FlyingRobotDuck extends ??? // awkward

// Composition: behaviors are pluggable
interface FlyBehavior { void fly(); }
class Duck {
  private FlyBehavior flyBehavior;
  void performFly() { flyBehavior.fly(); }
  void setFlyBehavior(FlyBehavior f) { this.flyBehavior = f; }
}` },
      { type: 'p', text: 'Head First Design Patterns popularized "favor composition over inheritance." Strategy, Decorator, and Bridge patterns are composition in disguise.' },
      { type: 'diagram', kind: 'pattern-strategy' },
      { type: 'h', text: 'When to use composition' },
      { type: 'ul', items: [
        'Behavior varies independently (payment types, pricing rules)',
        'You need runtime switching (elevator mode, game difficulty)',
        'Multiple dimensions of variation (coffee add-ons, not CoffeeWithMilkWithSugar class explosion)',
      ]},
      { type: 'h', text: 'When inheritance is still fine' },
      { type: 'ul', items: [
        'True "is-a" with stable contract (ChessPiece hierarchy with shared move interface)',
        'Framework hooks (abstract base with template method)',
        'Small, shallow trees with no behavioral mixing',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'If interviewer pushes "why not extend Vehicle for Car, Truck, ElectricCar," explain LSP + combinatorial explosion, then show composed Engine + DriveStrategy instead.' },
    ],
    references: [
      { label: 'Composition vs Inheritance — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/strategy' },
      { label: 'Oracle — Inheritance vs Composition', url: 'https://docs.oracle.com/javase/tutorial/java/IandI/subclasses.html' },
    ],
  },

  inheritance: {
    title: 'Inheritance',
    subtitle: 'Reuse code through "is-a" relationships — and know when to stop.',
    readingMinutes: 7,
    blocks: [
      { type: 'p', text: 'Inheritance lets a subclass extend a superclass: it inherits fields and methods and can override behavior. Use it when the relationship is genuine specialization, not convenience.' },
      { type: 'h', text: 'Building blocks' },
      { type: 'ul', items: [
        'extends (class) / implements (interface) in Java',
        'super() to call parent constructor',
        '@Override for replaced methods',
        'protected members visible to subclasses only',
      ]},
      { type: 'code', language: 'java', code: `abstract class ChessPiece {
  protected Color color;
  abstract boolean canMove(Board b, Square from, Square to);
  void move(Board b, Square from, Square to) {
    if (canMove(b, from, to)) b.movePiece(from, to);
  }
}

class Rook extends ChessPiece {
  boolean canMove(Board b, Square from, Square to) {
    return b.isClearRankOrFile(from, to);
  }
}` },
      { type: 'p', text: 'Prefer abstract classes when you share code + partial contract; prefer interfaces when you only need a capability contract (`Payable`, `Serializable`). Java allows one extends, many implements.' },
      { type: 'callout', tone: 'warn', title: 'Liskov reminder', text: 'Subclasses must not weaken preconditions or strengthen postconditions in ways that break callers. If Square cannot replace Rectangle in area code, the inheritance is wrong.' },
      { type: 'h', text: 'Interview checklist' },
      { type: 'ol', items: [
        'Is this truly is-a?',
        'Will subclasses need different fields?',
        'Could Strategy + composition avoid override chains?',
        'Are you overriding just to disable parent behavior? (red flag)',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Mention "protected template method in base, concrete steps in subclasses" for problems like Logger (console vs file sink) — shows you know both inheritance and patterns.' },
    ],
    references: [
      { label: 'Inheritance — Oracle Java Tutorial', url: 'https://docs.oracle.com/javase/tutorial/java/IandI/subclasses.html' },
      { label: 'Template Method Pattern — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/template-method' },
    ],
  },

  'encapsulation-and-abstraction': {
    title: 'Encapsulation & Abstraction',
    subtitle: 'Hide details, expose a simple contract — the heart of maintainable OO design.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Encapsulation bundles data with methods that control access (private fields, public getters/setters with validation). Abstraction presents a simplified model — you know what `elevator.requestFloor(5)` does without seeing motor logic.' },
      { type: 'h', text: 'Encapsulation in practice' },
      { type: 'code', language: 'javascript', code: `class BankAccount {
  #balance = 0; // private field

  deposit(amount) {
    if (amount <= 0) throw new Error('Invalid amount');
    this.#balance += amount;
  }

  withdraw(amount) {
    if (amount > this.#balance) throw new Error('Insufficient funds');
    this.#balance -= amount;
  }

  getBalance() { return this.#balance; }
}` },
      { type: 'p', text: 'Abstraction appears as interfaces, abstract classes, and service boundaries. `Storage` might expose `save()` and `findById()` while hiding SQL, indexing, and connection pooling.' },
      { type: 'h', text: 'Why interviewers care' },
      { type: 'ul', items: [
        'Encapsulation localizes change — swap DB without touching UI',
        'Abstraction enables testing — mock PaymentGateway in unit tests',
        'Together they prevent "train wreck" code: `user.getWallet().getCard().charge()` leaking internals',
      ]},
      { type: 'diagram', kind: 'class-diagram' },
      { type: 'callout', tone: 'tip', title: 'Law of Demeter', text: 'A method should talk to its friends, not strangers. Prefer `order.checkout()` over reaching through five getters.' },
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'When designing ATM, keep PIN inside Account/session object — never pass raw PIN strings through five classes. Interviewers notice security-aware encapsulation.' },
    ],
    references: [
      { label: 'Encapsulation — Oracle Java Tutorial', url: 'https://docs.oracle.com/javase/tutorial/java/javaOO/accesscontrol.html' },
      { label: 'Abstraction — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/abstraction-in-java-2/' },
    ],
  },

  factory: {
    title: 'Factory Pattern',
    subtitle: 'Centralize object creation so callers stay decoupled from concrete classes.',
    readingMinutes: 9,
    blocks: [
      { type: 'p', text: 'Intent: move `new ConcreteClass()` out of business logic into a dedicated creator. Callers ask for a product by type/name and receive an object matching an interface.' },
      { type: 'diagram', kind: 'pattern-factory' },
      { type: 'h', text: 'Simple Factory vs Factory Method vs Abstract Factory' },
      { type: 'table', headers: ['Variant', 'Idea', 'When'], rows: [
        ['Simple Factory', 'One static method picks concrete class', 'Small apps, few product types'],
        ['Factory Method', 'Subclass decides which product to create', 'Framework extension points'],
        ['Abstract Factory', 'Factory creates families of related products', 'UI themes, cross-platform widgets'],
      ]},
      { type: 'code', language: 'java', code: `interface Vehicle { void drive(); }

class Car implements Vehicle { public void drive() { /* ... */ } }
class Truck implements Vehicle { public void drive() { /* ... */ } }

class VehicleFactory {
  static Vehicle create(String type) {
    return switch (type) {
      case "CAR" -> new Car();
      case "TRUCK" -> new Truck();
      default -> throw new IllegalArgumentException(type);
    };
  }
}

// Client code — no direct new Car()
Vehicle v = VehicleFactory.create("CAR");` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Creation logic is complex (read config, pool connections)',
        'You want Open/Closed — add new product without editing every caller',
        'You must hide concrete types (library module boundaries)',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Only one implementation ever exists',
        'Creation is trivial and unlikely to change',
        'Over-abstracting a two-line `new` hurts readability',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Parking lot: `VehicleFactory.create(type)` for Car, Bike, Truck spot assignment. Tie to SRP: creation logic lives in one place.' },
    ],
    references: [
      { label: 'Factory Method — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/factory-method' },
      { label: 'Abstract Factory — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/abstract-factory' },
    ],
  },

  builder: {
    title: 'Builder Pattern',
    subtitle: 'Construct complex objects step by step with readable, validated assembly.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: separate construction of a complex object from its representation. Useful when an object has many optional fields or requires ordered setup steps.' },
      { type: 'h', text: 'Structure' },
      { type: 'ul', items: [
        'Builder — step methods like `setEngine()`, `addSeat()`',
        'Director (optional) — defines build order',
        'Product — immutable or fully configured object',
        'build() — validates and returns product',
      ]},
      { type: 'code', language: 'java', code: `class Pizza {
  private final String size, crust, topping;
  private Pizza(Builder b) { size = b.size; crust = b.crust; topping = b.topping; }

  static class Builder {
    private String size, crust, topping;
    Builder size(String s) { this.size = s; return this; }
    Builder crust(String c) { this.crust = c; return this; }
    Builder topping(String t) { this.topping = t; return this; }
    Pizza build() {
      if (size == null) throw new IllegalStateException("size required");
      return new Pizza(this);
    }
  }
}

Pizza p = new Pizza.Builder().size("L").crust("thin").topping("pepperoni").build();` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Telescoping constructor anti-pattern (too many overloads)',
        'Immutable objects with many optional parameters',
        'SQL/HTTP request builders, game character configs',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Simple POJO with 2–3 fields — use constructor or record',
        'Language has named parameters / dataclasses that solve it cleanly',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'BookMyShow: `BookingBuilder` with show, seats, user, payment — validate "at least one seat" in build(). Fluent chaining impresses if kept readable.' },
    ],
    references: [
      { label: 'Builder — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/builder' },
      { label: 'Effective Java — Builder pattern (Joshua Bloch)', url: 'https://docs.oracle.com/javase/tutorial/java/javaOO/builderpattern.html' },
    ],
  },

  singleton: {
    title: 'Singleton Pattern',
    subtitle: 'Exactly one instance — use sparingly and thread-safely.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: ensure a class has only one instance and provide global access. Common for config loaders, connection pools, or logging registries — but overuse creates hidden dependencies and hurts testing.' },
      { type: 'h', text: 'Thread-safe lazy initialization (Java)' },
      { type: 'code', language: 'java', code: `class Config {
  private static volatile Config instance;

  private Config() { /* load config once */ }

  static Config getInstance() {
    if (instance == null) {
      synchronized (Config.class) {
        if (instance == null) instance = new Config();
      }
    }
    return instance;
  }
}` },
      { type: 'p', text: 'In modern Java, enum singleton is often preferred: `enum Config { INSTANCE; }` — serialization-safe and concise.' },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Truly one shared resource (ID generator, app-wide cache with coordination)',
        'Expensive initialization that must happen once',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Makes unit tests hard (global mutable state)',
        'Dependency injection containers manage lifecycle better',
        'Distributed systems need cluster-wide singleton — not a Java static',
      ]},
      { type: 'callout', tone: 'warn', title: 'Interview red flag', text: 'Do not make every service a Singleton. Say: "I would use DI in production; Singleton here for simplicity in LLD."' },
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Logging framework topic: LoggerFactory as registry of named loggers — discuss why full Singleton on Logger is wrong (one per class).' },
    ],
    references: [
      { label: 'Singleton — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/singleton' },
      { label: 'Singleton — GeeksforGeeks (thread safety)', url: 'https://www.geeksforgeeks.org/singleton-design-pattern/' },
    ],
  },

  observer: {
    title: 'Observer Pattern',
    subtitle: 'Notify dependents automatically when subject state changes.',
    readingMinutes: 9,
    blocks: [
      { type: 'p', text: 'Intent: define a one-to-many dependency so when one object (subject) changes state, all dependents (observers) get updated. Powers event systems, UI bindings, and pub/sub at small scale.' },
      { type: 'diagram', kind: 'pattern-observer' },
      { type: 'h', text: 'Structure' },
      { type: 'ul', items: [
        'Subject — attach(), detach(), notifyObservers()',
        'Observer — update(event) or onChange(data)',
        'ConcreteSubject holds state; ConcreteObservers react',
      ]},
      { type: 'code', language: 'javascript', code: `class StockTicker {
  #price = 0;
  #observers = [];

  subscribe(obs) { this.#observers.push(obs); }
  setPrice(p) {
    this.#price = p;
    this.#observers.forEach(o => o.update(p));
  }
}

class EmailAlert {
  update(price) { console.log('Email: price now', price); }
}

const ticker = new StockTicker();
ticker.subscribe(new EmailAlert());
ticker.setPrice(142.5);` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Loose coupling between event source and handlers',
        'Multiple reactions to same event (Splitwise expense added → notify group)',
        'Domain events inside a single process',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Order of notification matters but is hard to control',
        'Risk of memory leaks if observers not unsubscribed',
        'Cross-service events — use message queue instead',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Elevator: floor display and internal panel both observe `ElevatorState`. Mention push vs pull observer variants if time allows.' },
    ],
    references: [
      { label: 'Observer — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/observer' },
      { label: 'Java Observable/Observer (legacy note) — Oracle', url: 'https://docs.oracle.com/javase/9/docs/api/java/util/Observable.html' },
    ],
  },

  strategy: {
    title: 'Strategy Pattern',
    subtitle: 'Swap algorithms at runtime without changing the client.',
    readingMinutes: 9,
    blocks: [
      { type: 'p', text: 'Intent: define a family of algorithms, encapsulate each one, and make them interchangeable. The context delegates work to a Strategy interface.' },
      { type: 'diagram', kind: 'pattern-strategy' },
      { type: 'code', language: 'java', code: `interface PricingStrategy {
  double total(Cart cart);
}

class RegularPricing implements PricingStrategy {
  public double total(Cart c) { return c.subtotal(); }
}

class FestivalPricing implements PricingStrategy {
  public double total(Cart c) { return c.subtotal() * 0.8; }
}

class CheckoutService {
  private PricingStrategy strategy;
  void setStrategy(PricingStrategy s) { this.strategy = s; }
  double checkout(Cart cart) { return strategy.total(cart); }
}` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Many variants of same behavior (payment, compression, routing)',
        'Eliminate big switch/if-else chains',
        'Open/Closed — add new strategy class without editing context',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Only one algorithm forever',
        'Strategies need heavy shared state — design context carefully',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Parking lot: `ParkingFeeStrategy` for hourly vs flat rate. Snake game: `MovementStrategy` for normal vs wrap-around board.' },
      { type: 'callout', tone: 'tip', title: 'Relation to State', text: 'Strategy is chosen by client/config; State transitions often happen inside the object itself. Interviewers may ask — know the difference.' },
    ],
    references: [
      { label: 'Strategy — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/strategy' },
      { label: 'Strategy — Head First Design Patterns (concept)', url: 'https://refactoring.guru/design-patterns/strategy' },
    ],
  },

  decorator: {
    title: 'Decorator Pattern',
    subtitle: 'Add responsibilities to objects dynamically without subclass explosion.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: wrap a component in decorator objects that add behavior before or after delegating to the wrapped object. Same interface throughout — transparent stacking.' },
      { type: 'code', language: 'java', code: `interface Coffee { double cost(); String desc(); }

class SimpleCoffee implements Coffee {
  public double cost() { return 2.0; }
  public String desc() { return "Coffee"; }
}

abstract class CoffeeDecorator implements Coffee {
  protected Coffee wrapped;
  CoffeeDecorator(Coffee c) { this.wrapped = c; }
}

class Milk extends CoffeeDecorator {
  Milk(Coffee c) { super(c); }
  public double cost() { return wrapped.cost() + 0.5; }
  public String desc() { return wrapped.desc() + ", milk"; }
}

Coffee order = new Milk(new SimpleCoffee()); // $2.50` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Optional add-ons combine freely (coffee, streams, HTTP middleware)',
        'Avoid class per combination (CoffeeWithMilkAndSugar...)',
        'Behavior must be added/removed at runtime',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Decorators must preserve interface contract — awkward if core API keeps changing',
        'Deep stacks hurt debugging — limit layers or use composition root',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Coffee machine LLD is the classic Decorator answer. Contrast with inheritance: `MilkCoffee extends Coffee` does not compose multiple toppings cleanly.' },
      { type: 'p', text: 'In Java, `BufferedInputStream` wrapping `FileInputStream` is the textbook Decorator — same `InputStream` interface, behavior stacked layer by layer.' },
    ],
    references: [
      { label: 'Decorator — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/decorator' },
      { label: 'Java I/O Streams — Oracle (Decorator example)', url: 'https://docs.oracle.com/javase/tutorial/essential/io/streams.html' },
    ],
  },

  command: {
    title: 'Command Pattern',
    subtitle: 'Encapsulate requests as objects — undo, queue, and log become natural.',
    readingMinutes: 9,
    blocks: [
      { type: 'p', text: 'Intent: turn a request into a stand-alone object with execute() (and optionally undo()). Invoker holds commands; receiver performs actual work.' },
      { type: 'h', text: 'Structure' },
      { type: 'ul', items: [
        'Command — execute(), undo()',
        'Receiver — knows how to apply changes (Document, Light)',
        'Invoker — run, history stack for undo',
        'Client — creates commands and sets receiver',
      ]},
      { type: 'code', language: 'java', code: `interface Command { void execute(); void undo(); }

class TransferCommand implements Command {
  private Account from, to;
  private int amount;
  public void execute() { from.debit(amount); to.credit(amount); }
  public void undo() { to.debit(amount); from.credit(amount); }
}

class CommandInvoker {
  private Deque<Command> history = new ArrayDeque<>();
  void run(Command c) { c.execute(); history.push(c); }
  void undo() { if (!history.isEmpty()) history.pop().undo(); }
}` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Undo/redo (text editor, chess move history)',
        'Job queues and macro recording',
        'Decouple UI buttons from business logic',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Simple one-shot actions with no undo/logging needs',
        'Every command class feels heavy for CRUD-only APIs',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Remote control / smart home: each button is a Command. Tic-tac-toe: moves as commands for replay. Mention idempotent undo constraints.' },
    ],
    references: [
      { label: 'Command — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/command' },
      { label: 'Command pattern — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/command-pattern/' },
    ],
  },

  state: {
    title: 'State Pattern',
    subtitle: 'Let an object change behavior when its internal state changes.',
    readingMinutes: 9,
    blocks: [
      { type: 'p', text: 'Intent: replace large conditionals on state with separate state classes. Context delegates to current State object; transitions swap the active state.' },
      { type: 'code', language: 'java', code: `interface VendingState {
  void insertCoin(VendingMachine vm);
  void dispense(VendingMachine vm);
}

class IdleState implements VendingState {
  public void insertCoin(VendingMachine vm) {
    vm.addBalance(1);
    vm.setState(new HasMoneyState());
  }
  public void dispense(VendingMachine vm) { /* no op */ }
}

class VendingMachine {
  private VendingState state = new IdleState();
  void setState(VendingState s) { this.state = s; }
  void insertCoin() { state.insertCoin(this); }
  void dispense() { state.dispense(this); }
}` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Object behavior depends on state and transitions are explicit',
        'ATM session states, elevator direction, order workflow',
        'switch grows with every new state',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Two states and simple flag — YAGNI',
        'States share almost all logic — Strategy may fit better',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Contrast State vs Strategy: State objects often trigger transitions themselves; Strategy is usually picked externally. Vending machine is the go-to example.' },
      { type: 'diagram', kind: 'pattern-strategy' },
    ],
    references: [
      { label: 'State — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/state' },
      { label: 'State pattern — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/state-design-pattern/' },
    ],
  },

  adapter: {
    title: 'Adapter Pattern',
    subtitle: 'Make an incompatible interface work with existing client code.',
    readingMinutes: 7,
    blocks: [
      { type: 'p', text: 'Intent: convert the interface of a class into another interface clients expect. Like a power adapter — European plug meets US socket.' },
      { type: 'code', language: 'java', code: `// Legacy third-party payment SDK
class LegacyPay {
  void sendPayment(double dollars) { /* ... */ }
}

// Your domain expects this
interface PaymentProcessor {
  void pay(Money amount);
}

class LegacyPayAdapter implements PaymentProcessor {
  private LegacyPay legacy;
  public void pay(Money amount) {
    legacy.sendPayment(amount.toDollars());
  }
}` },
      { type: 'h', text: 'Object vs class adapter' },
      { type: 'ul', items: [
        'Object adapter — composition (wrap legacy instance) — preferred in Java',
        'Class adapter — inheritance from both — rare, multiple inheritance limits',
      ]},
      { type: 'h', text: 'When to use / not use' },
      { type: 'ul', items: [
        'Use: integrate SDKs, old APIs, external auth providers',
        'Not: greenfield where you control both sides — align interfaces instead',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'BookMyShow + external payment gateway = Adapter. Clarify you are not changing gateway code — wrapping it.' },
      { type: 'p', text: 'Adapter differs from Facade: Adapter fixes one interface mismatch; Facade simplifies many calls into one. Do not confuse wrapping one class with orchestrating a subsystem.' },
    ],
    references: [
      { label: 'Adapter — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/adapter' },
    ],
  },

  facade: {
    title: 'Facade Pattern',
    subtitle: 'One simple entry point over a complex subsystem.',
    readingMinutes: 7,
    blocks: [
      { type: 'p', text: 'Intent: provide a unified high-level interface to a set of interfaces in a subsystem. Clients talk to Facade; Facade coordinates internal classes.' },
      { type: 'code', language: 'javascript', code: `class InventoryService { reserve(sku, qty) { /* ... */ } }
class PaymentService { charge(user, amount) { /* ... */ } }
class ShippingService { ship(order) { /* ... */ } }

class OrderFacade {
  placeOrder(cart, user) {
    this.inventory.reserve(cart.items);
    this.payment.charge(user, cart.total());
    this.shipping.ship(cart.toOrder(user));
    return { status: 'CONFIRMED' };
  }
}

// Client: one call instead of three
new OrderFacade().placeOrder(cart, user);` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Simplify onboarding for common workflows',
        'Layer boundaries (API controller as facade over services)',
        'Reduce coupling from UI to many micro-services internally',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Facade becomes God class with all business rules — split use cases',
        'Clients need fine-grained control — expose lower APIs too',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Library management: `LibraryFacade.issueBook()` hides member validation, catalog lookup, and fine calculation.' },
      { type: 'diagram', kind: 'class-diagram' },
    ],
    references: [
      { label: 'Facade — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/facade' },
    ],
  },

  proxy: {
    title: 'Proxy Pattern',
    subtitle: 'Stand-in object that controls access to the real subject.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: provide a surrogate or placeholder to control access, add lazy loading, caching, logging, or security checks before reaching real object.' },
      { type: 'h', text: 'Common proxy types' },
      { type: 'table', headers: ['Type', 'Purpose', 'Example'], rows: [
        ['Virtual', 'Lazy load expensive object', 'Load high-res image on demand'],
        ['Protection', 'Access control', 'Admin-only delete'],
        ['Remote', 'Local stand-in for remote service', 'RPC stub'],
        ['Caching', 'Memoize results', 'DB query proxy'],
      ]},
      { type: 'code', language: 'java', code: `interface Image {
  void display();
}

class RealImage implements Image {
  RealImage(String file) { loadFromDisk(file); }
  public void display() { /* show pixels */ }
}

class ProxyImage implements Image {
  private RealImage real;
  private String file;
  public void display() {
    if (real == null) real = new RealImage(file); // lazy
    real.display();
  }
}` },
      { type: 'h', text: 'Proxy vs Decorator' },
      { type: 'p', text: 'Both wrap objects. Proxy controls access/lifecycle; Decorator adds behavior while expecting subject to exist. Proxies often manage creation; decorators enhance existing instance.' },
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'ATM: proxy bank service with retry + circuit breaker narrative. Mention Spring @Transactional as infrastructure proxy if interviewer knows Spring.' },
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Simple pass-through with no extra behavior — call real object directly',
        'Caching proxy without eviction policy — creates stale data bugs',
      ]},
    ],
    references: [
      { label: 'Proxy — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/proxy' },
    ],
  },

  'chain-of-responsibility': {
    title: 'Chain of Responsibility',
    subtitle: 'Pass a request along a chain until someone handles it.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: avoid coupling sender to a single receiver by giving multiple handlers a chance to process the request sequentially.' },
      { type: 'code', language: 'java', code: `abstract class SupportHandler {
  protected SupportHandler next;
  void setNext(SupportHandler n) { next = n; }
  void handle(Ticket t) {
    if (canHandle(t)) resolve(t);
    else if (next != null) next.handle(t);
    else escalate(t);
  }
  abstract boolean canHandle(Ticket t);
  abstract void resolve(Ticket t);
}

class L1Support extends SupportHandler {
  boolean canHandle(Ticket t) { return t.priority() <= 2; }
  void resolve(Ticket t) { /* ... */ }
}

class L2Support extends SupportHandler { /* ... */ }` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Middleware pipelines (HTTP filters, logging levels)',
        'Approval workflows with increasing authority',
        'Event bubbling in UI frameworks',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Exactly one handler must run — use Strategy instead',
        'Order of handlers is unclear — document chain assembly',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Logging framework: DEBUG → INFO → WARN handlers, or filter chain that decides minimum level per appender.' },
      { type: 'p', text: 'Servlet filters and Spring Security filter chains are real-world Chain of Responsibility — each link can short-circuit or pass to next.' },
    ],
    references: [
      { label: 'Chain of Responsibility — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/chain-of-responsibility' },
    ],
  },

  visitor: {
    title: 'Visitor Pattern',
    subtitle: 'Add new operations to object structures without changing their classes.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: separate algorithms from the object structure they traverse. Each element accepts a Visitor; double dispatch picks the right visit method.' },
      { type: 'code', language: 'java', code: `interface CartItem { void accept(PricingVisitor v); }

class Book implements CartItem {
  private double price;
  public void accept(PricingVisitor v) { v.visit(this); }
}

class PricingVisitor {
  private double total;
  void visit(Book b) { total += b.getPrice() * 0.9; } // 10% off books
  void visit(Electronics e) { total += e.getPrice(); }
  double getTotal() { return total; }
}` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Stable class hierarchy, many new operations (export XML, JSON, tax report)',
        'Compiler AST passes — classic textbook use case',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Hierarchy changes often — every new element breaks all visitors',
        'Simple collections — plain functions or polymorphism suffice',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Rare in 45-min LLD unless problem involves "many reports on same object tree." Mention trade-off: Open/Closed for operations, closed for new element types.' },
      { type: 'formula', text: 'Double dispatch: element.accept(visitor) → visitor.visit(concreteElement)' },
    ],
    references: [
      { label: 'Visitor — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/visitor' },
    ],
  },

  'template-method': {
    title: 'Template Method Pattern',
    subtitle: 'Define algorithm skeleton in base class; subclasses fill specific steps.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: in an abstract class, define the non-overridable templateMethod() that calls hook/abstract steps. Subclasses customize steps without changing overall flow.' },
      { type: 'code', language: 'java', code: `abstract class DataParser {
  // Template method
  public final ParsedData parse(InputStream in) {
    String raw = read(in);
    String normalized = normalize(raw);
    return mapToObject(normalized);
  }
  protected abstract String read(InputStream in);
  protected String normalize(String raw) { return raw.trim(); } // hook
  protected abstract ParsedData mapToObject(String raw);
}

class CsvParser extends DataParser {
  protected String read(InputStream in) { /* read CSV */ return ""; }
  protected ParsedData mapToObject(String raw) { /* ... */ return null; }
}` },
      { type: 'h', text: 'When to use' },
      { type: 'ul', items: [
        'Shared workflow with variant steps (build pipeline, game turn loop)',
        'Prevent subclasses from breaking algorithm order — final template method',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Steps vary in order per subclass — composition/Strategy fits better',
        'Deep inheritance for one hook — overkill',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Chess: `Game.playTurn()` template — validate move, apply, check checkmate, switch player. Hooks in Chess vs Checkers subclasses.' },
      { type: 'p', text: 'Mark the template method `final` so subclasses cannot skip validation steps — interviewers like hearing you protect invariants in the base class.' },
    ],
    references: [
      { label: 'Template Method — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/template-method' },
    ],
  },

  repository: {
    title: 'Repository Pattern',
    subtitle: 'Collection-like access to domain objects hiding persistence details.',
    readingMinutes: 8,
    blocks: [
      { type: 'p', text: 'Intent: mediate between domain and data mapping layers using a interface that feels like an in-memory collection: findById, save, delete, query.' },
      { type: 'code', language: 'java', code: `interface UserRepository {
  Optional<User> findById(String id);
  List<User> findByEmail(String email);
  void save(User user);
  void delete(String id);
}

class InMemoryUserRepository implements UserRepository {
  private Map<String, User> store = new HashMap<>();
  public Optional<User> findById(String id) { return Optional.ofNullable(store.get(id)); }
  public void save(User user) { store.put(user.getId(), user); }
  // ...
}

class JpaUserRepository implements UserRepository {
  // EntityManager / JDBC implementation
}` },
      { type: 'h', text: 'Benefits in LLD' },
      { type: 'ul', items: [
        'Service layer stays persistence-agnostic',
        'Easy to swap in-memory store for interview demo vs DB in production',
        'Centralizes query logic (no SQL scattered in controllers)',
      ]},
      { type: 'h', text: 'When not to use' },
      { type: 'ul', items: [
        'Simple CRUD with framework already abstracting (Spring Data) — do not reinvent',
        'Heavy reporting queries — add read models or CQRS instead of fat repository',
      ]},
      { type: 'callout', tone: 'interview', title: 'Interview tip', text: 'Almost every design problem needs `XRepository` for persistence. Say: "Interview in-memory; production would be Postgres + same interface."' },
      { type: 'diagram', kind: 'class-diagram' },
    ],
    references: [
      { label: 'Repository pattern — Martin Fowler (concept)', url: 'https://martinfowler.com/eaaCatalog/repository.html' },
      { label: 'DAO vs Repository — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/dao-design-pattern/' },
    ],
  },

  'parking-lot': {
    title: 'Parking Lot LLD',
    subtitle: 'Classic machine-coding problem — vehicles, spots, tickets, and fees.',
    readingMinutes: 12,
    blocks: [
      { type: 'p', text: 'Design a multi-level parking lot that parks cars, bikes, and trucks in appropriately sized spots, issues entry tickets, calculates fees on exit, and supports future payment types.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Multiple floors; each floor has many spots (compact, large, oversized)',
        'Entry gate assigns ticket with vehicle + timestamp',
        'Exit gate computes fee and frees spot',
        'Only compatible vehicle type can use a spot',
        'Optional: display board showing free spots per floor',
      ]},
      { type: 'diagram', kind: 'parking-lot' },
      { type: 'h', text: 'Core classes' },
      { type: 'table', headers: ['Class', 'Role'], rows: [
        ['ParkingLot', 'Singleton/facade orchestrating entry & exit'],
        ['ParkingFloor', 'Collection of spots on one level'],
        ['ParkingSpot', 'State: free/occupied; size type'],
        ['Vehicle', 'Abstract — Car, Bike, Truck'],
        ['Ticket', 'id, entryTime, spotRef, vehicle'],
        ['FeeStrategy', 'Hourly / flat pricing'],
        ['EntryPanel / ExitPanel', 'Gate hardware interface'],
      ]},
      { type: 'code', language: 'java', code: `class ParkingLot {
  List<ParkingFloor> floors;
  FeeStrategy feeStrategy;

  Ticket park(Vehicle v) {
    ParkingSpot spot = findNearestFreeSpot(v);
    if (spot == null) throw new FullException();
    spot.park(v);
    return new Ticket(spot, v, Instant.now());
  }

  Money exit(Ticket t) {
    t.getSpot().unpark();
    Duration stay = Duration.between(t.getEntryTime(), Instant.now());
    return feeStrategy.calculate(stay, t.getVehicle());
  }
}` },
      { type: 'h', text: 'Extensibility' },
      { type: 'ul', items: [
        'New vehicle type → extend Vehicle + spot matching rule',
        'New payment → Strategy on exit flow',
        'Electric charging spots → decorator or subclass on ParkingSpot',
      ]},
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Concurrent entry on last spot (locking), handicapped spots, reservation system, ANPR camera integration at gate.' },
    ],
    references: [
      { label: 'Parking Lot LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/parking-lot-system-design/' },
      { label: 'Factory + Strategy in parking — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/catalog' },
    ],
  },

  bookmyshow: {
    title: 'BookMyShow LLD',
    subtitle: 'Movie ticket booking — seats, locks, payments, and concurrency.',
    readingMinutes: 12,
    blocks: [
      { type: 'p', text: 'Design a system where users browse shows, select seats, temporarily lock them, pay, and receive confirmed bookings. Handle seat conflicts when two users pick the same seat.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Cities → Theaters → Screens → Shows (movie + time)',
        'Seat map with available / locked / booked states',
        'Seat lock expires after N minutes',
        'One user can book multiple seats in one transaction',
        'Payment success confirms booking; failure releases lock',
      ]},
      { type: 'h', text: 'Key classes & methods' },
      { type: 'table', headers: ['Class', 'Key methods'], rows: [
        ['Show', 'getSeatMap(), lockSeats(ids, user), confirmBooking()'],
        ['Seat', 'id, type (gold/silver), status enum'],
        ['Booking', 'user, show, seats, paymentStatus'],
        ['SeatLockService', 'acquireLock(), releaseExpired()'],
        ['PaymentService', 'pay(booking) — Adapter to gateway'],
        ['BookingService', 'orchestrates lock → pay → confirm'],
      ]},
      { type: 'code', language: 'java', code: `class BookingService {
  Booking bookShow(User user, Show show, List<String> seatIds) {
    LockToken token = show.lockSeats(seatIds, user, Duration.ofMinutes(7));
    try {
      paymentService.charge(user, show.priceFor(seatIds));
      return show.confirmBooking(token);
    } catch (PaymentFailed e) {
      show.releaseLock(token);
      throw e;
    }
  }
}` },
      { type: 'h', text: 'Concurrency approach' },
      { type: 'p', text: 'Use optimistic locking (version on seat row) or synchronized block per show when flipping seat status. Mention distributed lock (Redis) if interviewer scales to multiple app servers.' },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Waitlist, cancellation refunds, dynamic pricing, search/filter APIs, admin show scheduling.' },
    ],
    references: [
      { label: 'BookMyShow LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-bookmyshow-system-low-level/' },
      { label: 'Facade + Adapter patterns — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/facade' },
    ],
  },

  chess: {
    title: 'Chess LLD',
    subtitle: 'Model board, pieces, legal moves, check, and checkmate.',
    readingMinutes: 11,
    blocks: [
      { type: 'p', text: 'Design classes to represent a two-player chess game: setup board, move pieces with validation, detect check/checkmate/stalemate, and support undo or move history.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Standard 8×8 board, two players, alternating turns',
        'Each piece type moves differently; capture by moving to occupied square',
        'Block illegal moves (moving into check, invalid piece paths)',
        'Detect game end conditions',
      ]},
      { type: 'h', text: 'Class design' },
      { type: 'ul', items: [
        'Board — 8×8 grid, pieceAt(square), movePiece(from, to)',
        'ChessPiece (abstract) — color, canMove(board, from, to)',
        'Concrete pieces — Pawn, Rook, Knight, Bishop, Queen, King',
        'Game — currentPlayer, makeMove(), status enum',
        'Move — from, to, optional promotion piece',
      ]},
      { type: 'code', language: 'java', code: `class Game {
  Board board = new Board();
  Color turn = Color.WHITE;

  void makeMove(Move m) {
    Piece p = board.get(m.from());
    if (p == null || p.color != turn) throw new IllegalMoveException();
    if (!p.canMove(board, m.from(), m.to())) throw new IllegalMoveException();
    board.apply(m);
    if (board.isKingInCheck(opponent(turn))) { /* validate not left self in check */ }
    turn = opponent(turn);
    if (board.isCheckmate(turn)) status = Status.CHECKMATE;
  }
}` },
      { type: 'callout', tone: 'tip', title: 'Extensibility', text: 'MoveValidator service keeps piece classes simpler. Command pattern stores moves for undo.' },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Castling, en passant, pawn promotion, draw by repetition, AI player (Strategy for evaluation).' },
    ],
    references: [
      { label: 'Chess game LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-chess-game/' },
      { label: 'Template Method — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/template-method' },
    ],
  },

  atm: {
    title: 'ATM LLD',
    subtitle: 'Cash dispenser, PIN auth, accounts, and transaction states.',
    readingMinutes: 11,
    blocks: [
      { type: 'p', text: 'Design an ATM connected to a bank: insert card, authenticate PIN, check balance, withdraw cash, deposit, and print mini-statement. Handle hardware failures gracefully.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'User identified by card + PIN (limited retries)',
        'Withdraw only if account balance and ATM cash sufficient',
        'Dispense notes in optimal denomination mix when possible',
        'Log all transactions',
      ]},
      { type: 'h', text: 'Core classes' },
      { type: 'table', headers: ['Class', 'Role'], rows: [
        ['ATM', 'State machine + coordinates hardware'],
        ['BankService', 'validatePin, debit, credit (Proxy to core bank)'],
        ['Account', 'balance, accountNumber'],
        ['CashDispenser', 'inventory of note counts'],
        ['CardReader / Screen / Printer', 'Hardware interfaces'],
        ['Transaction', 'type, amount, timestamp, status'],
      ]},
      { type: 'code', language: 'java', code: `class ATM {
  ATMState state = new IdleState();
  BankService bank;
  CashDispenser dispenser;

  void insertCard(Card card) { state.insertCard(this, card); }
  void enterPin(String pin) { state.enterPin(this, pin); }
  void withdraw(int amount) { state.withdraw(this, amount); }
}

class WithdrawState implements ATMState {
  void withdraw(ATM atm, int amount) {
    if (!atm.dispenser.canDispense(amount)) throw new InsufficientCashException();
    atm.bank.debit(atm.session.account(), amount);
    atm.dispenser.dispense(amount);
    atm.setState(new IdleState());
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Concurrent ATMs on same account (locking), OTP instead of PIN, multi-language UI, admin refill cash.' },
      { type: 'steps', steps: [
        { title: 'Insert card', text: 'Idle → CardInserted; read card id.' },
        { title: 'Enter PIN', text: 'Validate via BankService; track retries.' },
        { title: 'Choose transaction', text: 'Withdraw / deposit / balance.' },
        { title: 'Complete', text: 'Eject card, print receipt, return to Idle.' },
      ]},
    ],
    references: [
      { label: 'ATM LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-an-atm-machine/' },
      { label: 'State pattern — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/state' },
    ],
  },

  splitwise: {
    title: 'Splitwise LLD',
    subtitle: 'Split expenses, track balances, and simplify who owes whom.',
    readingMinutes: 11,
    blocks: [
      { type: 'p', text: 'Design an expense-sharing app: users create groups, add bills split equally or by exact amounts, see net balances, and settle up between friends.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Users can belong to multiple groups',
        'Expense has payer, amount, split type (equal, exact, percentage)',
        'Show balance sheet: who owes whom how much',
        'Optional: simplify debts to minimum transactions',
      ]},
      { type: 'h', text: 'Class design' },
      { type: 'ul', items: [
        'User — id, name, email',
        'Group — members, expenses list',
        'Expense — amount, payer, SplitStrategy, participants',
        'SplitStrategy — interface with equal/exact/percent implementations',
        'BalanceSheet — compute net between pairs or per user',
        'Settlement — record payment between two users',
      ]},
      { type: 'code', language: 'java', code: `class ExpenseService {
  void addExpense(Group g, Expense e) {
    Map<User, Money> shares = e.getSplitStrategy().split(e);
    for (var entry : shares.entrySet()) {
      g.recordShare(e.getPayer(), entry.getKey(), entry.getValue());
    }
    g.addExpense(e);
    notifyObservers(g); // Observer for push notifications
  }
}

interface SplitStrategy {
  Map<User, Money> split(Expense e);
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Debt simplification graph algorithm, currency conversion, expense edit history, non-group friend expenses.' },
      { type: 'diagram', kind: 'class-diagram' },
    ],
    references: [
      { label: 'Splitwise LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-splitwise/' },
      { label: 'Strategy pattern — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/strategy' },
    ],
  },

  elevator: {
    title: 'Elevator LLD',
    subtitle: 'Multiple elevators serving floors with scheduling and direction.',
    readingMinutes: 12,
    blocks: [
      { type: 'p', text: 'Design a building elevator system: external floor buttons, internal cabin buttons, multiple elevators, and a controller that assigns requests efficiently.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Building has N floors and M elevators',
        'Passengers request UP/DOWN from hallway; select destination inside cabin',
        'Elevator moves floor by floor, opens door at stops',
        'System minimizes wait time (any reasonable scheduling algo acceptable in interview)',
      ]},
      { type: 'diagram', kind: 'elevator' },
      { type: 'h', text: 'Core classes' },
      { type: 'table', headers: ['Class', 'Role'], rows: [
        ['Building', 'floors, elevator list, request queue'],
        ['Elevator', 'currentFloor, direction, state, openDoor()'],
        ['ElevatorController', 'assigns hall calls to elevators'],
        ['Request', 'source floor, direction, optional destination'],
        ['DispatcherStrategy', 'SCAN, nearest car, etc.'],
      ]},
      { type: 'code', language: 'java', code: `class Elevator {
  int currentFloor = 0;
  Direction direction = Direction.IDLE;
  PriorityQueue<Integer> upStops = new PriorityQueue<>();
  PriorityQueue<Integer> downStops = new PriorityQueue<>(Collections.reverseOrder());

  void step() {
    if (direction == Direction.UP && !upStops.isEmpty()) {
      currentFloor = upStops.poll();
      openDoor();
      if (upStops.isEmpty()) direction = Direction.IDLE;
    }
    // similar for DOWN
  }

  void addRequest(Request r) { /* enqueue stop */ }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Weight limit, emergency stop, maintenance mode, express elevators for penthouse, Observer for floor displays.' },
    ],
    references: [
      { label: 'Elevator LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-elevator-system/' },
      { label: 'State pattern — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/state' },
    ],
  },

  'library-management': {
    title: 'Library Management LLD',
    subtitle: 'Books, members, lending, fines, and catalog search.',
    readingMinutes: 11,
    blocks: [
      { type: 'p', text: 'Design a library system: catalog books, register members, issue/return copies, compute overdue fines, and reserve books when all copies are out.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Book title may have multiple physical copies (barcode each)',
        'Member can borrow up to N items',
        'Due date and daily fine after grace period',
        'Search by title, author, ISBN',
      ]},
      { type: 'h', text: 'Classes & interfaces' },
      { type: 'ul', items: [
        'Book / BookItem — metadata vs physical copy',
        'Member — borrow limit, active loans',
        'LendingService — issue(), return(), reserve()',
        'Catalog — search index (could use Repository)',
        'FineCalculator — Strategy by member type (student/staff)',
        'LibraryFacade — simple API for librarian desk',
      ]},
      { type: 'code', language: 'java', code: `class LendingService {
  Loan issue(Member m, BookItem copy) {
    if (m.activeLoans() >= m.getLimit()) throw new LimitExceeded();
    if (!copy.isAvailable()) throw new UnavailableException();
    copy.markIssued();
    return new Loan(m, copy, LocalDate.now().plusDays(14));
  }

  Money returnBook(Loan loan) {
    copy.markAvailable();
    return fineCalculator.compute(loan);
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Multiple branches, inter-library loan, digital ebooks, notification reminders, admin reports.' },
      { type: 'steps', steps: [
        { title: 'Search catalog', text: 'Member finds available copy by ISBN or title.' },
        { title: 'Issue book', text: 'Librarian validates membership and loan limit.' },
        { title: 'Return', text: 'Compute fine if overdue; mark copy available.' },
      ]},
    ],
    references: [
      { label: 'Library Management LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/library-management-system/' },
      { label: 'Facade pattern — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/facade' },
    ],
  },

  'inventory-management': {
    title: 'Inventory Management LLD',
    subtitle: 'SKUs, warehouses, stock movements, and low-stock alerts.',
    readingMinutes: 11,
    blocks: [
      { type: 'p', text: 'Design inventory for an e-commerce or retail backend: track stock per warehouse, process orders/reservations, record restocks, and prevent overselling.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Product (SKU) with quantity per warehouse location',
        'Reserve stock on order placement; deduct on shipment',
        'Restock and transfer between warehouses',
        'Alert when quantity below reorder threshold',
      ]},
      { type: 'h', text: 'Key classes' },
      { type: 'table', headers: ['Class', 'Role'], rows: [
        ['Product', 'sku, name, reorderLevel'],
        ['Warehouse', 'id, address, stock map'],
        ['InventoryService', 'reserve(), commit(), release(), transfer()'],
        ['StockMovement', 'audit log entry'],
        ['Order', 'line items referencing SKU + qty'],
      ]},
      { type: 'code', language: 'java', code: `class InventoryService {
  synchronized void reserve(Order order) {
    for (LineItem li : order.items()) {
      Stock s = warehouse.stock(li.sku());
      if (s.available() < li.qty()) throw new OutOfStockException(li.sku());
      s.reserve(li.qty());
    }
  }

  void commit(Order order) {
    for (LineItem li : order.items())
      warehouse.stock(li.sku()).deduct(li.qty());
    logMovements(order, MovementType.SHIP);
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Concurrent orders on last unit, batch/lot tracking, expiry dates, distributed inventory across regions.' },
      { type: 'callout', tone: 'tip', title: 'Design tip', text: 'Separate `available`, `reserved`, and `onHand` counts so you never double-sell during checkout vs shipment.' },
    ],
    references: [
      { label: 'Inventory System LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-inventory-management-system/' },
      { label: 'Repository pattern — Martin Fowler', url: 'https://martinfowler.com/eaaCatalog/repository.html' },
    ],
  },

  'snake-game': {
    title: 'Snake Game LLD',
    subtitle: 'Game board, snake growth, food spawn, and collision rules.',
    readingMinutes: 10,
    blocks: [
      { type: 'p', text: 'Design the object model for classic Snake: grid board, snake body as linked segments, food placement, score, and game-over on wall/self collision.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Grid of width × height',
        'Snake moves one cell per tick in current direction',
        'Eating food grows snake and increases score',
        'Cannot reverse direction instantly (e.g. UP → DOWN)',
        'Game ends on wall or self bite',
      ]},
      { type: 'h', text: 'Classes' },
      { type: 'ul', items: [
        'Game — game loop, score, status',
        'Board — grid cells, render state',
        'Snake — deque of Position, move(), grow()',
        'Direction — enum UP/DOWN/LEFT/RIGHT',
        'FoodGenerator — random empty cell',
        'InputHandler — maps keys to direction changes',
      ]},
      { type: 'code', language: 'javascript', code: `class SnakeGame {
  tick() {
    const next = head.move(this.direction);
    if (this.board.isWall(next) || this.snake.occupies(next)) {
      this.status = 'GAME_OVER';
      return;
    }
    this.snake.moveTo(next);
    if (this.board.foodAt(next)) {
      this.snake.grow();
      this.score += 10;
      this.board.spawnFood();
    }
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Multiple food types, speed levels (Strategy), wrap-around board, two-player mode, persist high scores.' },
      { type: 'h', text: 'Game loop' },
      { type: 'ol', items: [
        'Read input (queue direction changes)',
        'Update snake position one cell',
        'Check collisions and food',
        'Render board — repeat on timer tick',
      ]},
    ],
    references: [
      { label: 'Snake Game LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-snake-game/' },
      { label: 'State / Strategy — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/state' },
    ],
  },

  'coffee-machine': {
    title: 'Coffee Machine LLD',
    subtitle: 'Recipes, ingredients inventory, and decorator-style add-ons.',
    readingMinutes: 10,
    blocks: [
      { type: 'p', text: 'Design a coffee vending machine: select drink type, check ingredient levels (coffee, milk, sugar, water), brew beverage, and support condiment add-ons with updated price.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Menu of drinks (espresso, latte, cappuccino)',
        'Machine tracks ingredient inventory; reject if insufficient',
        'Optional add-ons (extra shot, soy milk)',
        'Display price before dispense; accept payment (coin/card stub)',
      ]},
      { type: 'h', text: 'Design' },
      { type: 'ul', items: [
        'Beverage (abstract) — cost(), recipe()',
        'Concrete drinks + Decorator for add-ons',
        'Inventory — decrement on brew',
        'CoffeeMachine — selectDrink(), brew(), refill()',
        'PaymentProcessor — Strategy',
      ]},
      { type: 'code', language: 'java', code: `class CoffeeMachine {
  Beverage selected;

  void selectBase(String type) {
    selected = menu.create(type); // Factory
  }

  void addOn(String addon) {
    selected = AddonFactory.wrap(selected, addon); // Decorator
  }

  void brew() {
    Recipe r = selected.recipe();
    inventory.consume(r);
    payment.charge(selected.cost());
    dispense(selected.desc());
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Cleaning cycle state, maintenance alerts, user accounts with prepaid wallet, recipe config without redeploy.' },
      { type: 'diagram', kind: 'pattern-factory' },
    ],
    references: [
      { label: 'Coffee Machine LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-a-coffee-vending-machine/' },
      { label: 'Decorator — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/decorator' },
    ],
  },

  'tic-tac-toe': {
    title: 'Tic Tac Toe LLD',
    subtitle: '3×3 board, two players, win/draw detection, and optional AI.',
    readingMinutes: 9,
    blocks: [
      { type: 'p', text: 'Design a tic-tac-toe game for two human players or human vs computer on a 3×3 grid with clear turn management and win detection.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Two symbols: X and O',
        'Players alternate placing on empty cells',
        'Win: three in row/col/diagonal; draw when board full',
        'Optional undo or restart',
      ]},
      { type: 'h', text: 'Classes' },
      { type: 'ul', items: [
        'Board — cells[3][3], place(row,col,symbol), isFull()',
        'Player — name, symbol',
        'Game — currentPlayer, makeMove(), winner',
        'WinChecker — scan lines after each move',
        'AIPlayer — Strategy (minimax for perfect play)',
      ]},
      { type: 'code', language: 'java', code: `class Game {
  Board board = new Board();
  Player current;

  void makeMove(int r, int c) {
    if (!board.isEmpty(r, c)) throw new IllegalMoveException();
    board.place(r, c, current.getSymbol());
    if (WinChecker.check(board, current.getSymbol())) {
      status = Status.WIN;
      winner = current;
    } else if (board.isFull()) {
      status = Status.DRAW;
    } else {
      current = other(current);
    }
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'N×N board generalization, online multiplayer sync, Command pattern for move history.' },
      { type: 'p', text: 'Keep win detection O(1) per move by tracking last placed cell and only checking lines through that cell — a small optimization interviewers appreciate.' },
    ],
    references: [
      { label: 'Tic Tac Toe LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-tic-tac-toe-game/' },
      { label: 'Command pattern — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/command' },
    ],
  },

  'logging-framework': {
    title: 'Logging Framework LLD',
    subtitle: 'Log levels, appenders, formatters, and logger hierarchy.',
    readingMinutes: 11,
    blocks: [
      { type: 'p', text: 'Design a logging library similar to log4j/slf4j basics: named loggers, levels (DEBUG–ERROR), multiple appenders (console, file), and configurable formatting.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Logger per class/name with hierarchical names (com.app.service)',
        'Filter by minimum level per logger/appender',
        'Multiple appenders per logger',
        'Thread-safe logging from concurrent code',
        'Configurable without recompiling (properties file stub ok)',
      ]},
      { type: 'h', text: 'Structure' },
      { type: 'ul', items: [
        'Logger — debug/info/warn/error methods',
        'LoggerFactory — getLogger(name), cache instances',
        'LogRecord — timestamp, level, message, thread',
        'Appender — append(LogRecord) — Console, File',
        'Formatter — format record to string',
        'Filter / Level — Chain of Responsibility optional',
      ]},
      { type: 'code', language: 'java', code: `class Logger {
  String name;
  Level level = Level.INFO;
  List<Appender> appenders = new ArrayList<>();

  void log(Level l, String msg) {
    if (l.isLowerThan(this.level)) return;
    LogRecord rec = new LogRecord(l, msg, name, Thread.currentThread());
    for (Appender a : appenders) a.append(rec);
  }

  void info(String msg) { log(Level.INFO, msg); }
}

class LoggerFactory {
  static Map<String, Logger> cache = new ConcurrentHashMap<>();
  static Logger getLogger(String name) {
    return cache.computeIfAbsent(name, Logger::new);
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Async appenders (queue + worker), rolling file by size/date, MDC context fields, performance lazy evaluation.' },
      { type: 'table', headers: ['Level', 'Typical use'], rows: [
        ['DEBUG', 'Developer troubleshooting detail'],
        ['INFO', 'Normal business events'],
        ['WARN', 'Recoverable problems'],
        ['ERROR', 'Failures needing attention'],
      ]},
    ],
    references: [
      { label: 'Java Logging Overview — Oracle', url: 'https://docs.oracle.com/javase/8/docs/technotes/guides/logging/overview.html' },
      { label: 'Chain of Responsibility — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/chain-of-responsibility' },
    ],
  },

  'vending-machine': {
    title: 'Vending Machine LLD',
    subtitle: 'Product slots, coin inventory, change return, and state-driven flow.',
    readingMinutes: 11,
    blocks: [
      { type: 'p', text: 'Design a vending machine: display products, accept coins/cash, dispense item, return change, and handle out-of-stock or insufficient payment without losing money.' },
      { type: 'h', text: 'Requirements' },
      { type: 'ul', items: [
        'Rows of products with price and quantity',
        'Accept coins; track inserted balance',
        'Cancel returns inserted coins',
        'Dispense product + change on success',
        'Reject when empty or underpaid',
      ]},
      { type: 'h', text: 'State pattern flow' },
      { type: 'ol', items: [
        'Idle — waiting for coin or selection',
        'HasMoney — accept selection or refund',
        'Dispensing — release product',
        'ReturnChange — eject coins',
      ]},
      { type: 'diagram', kind: 'pattern-factory' },
      { type: 'h', text: 'Core classes' },
      { type: 'ul', items: [
        'VendingMachine — context + current state',
        'Inventory — Map<Slot, ProductBundle>',
        'CoinInventory — available change denominations',
        'Product — code, price, count',
        'VendingState — insertMoney, selectProduct, dispense',
      ]},
      { type: 'code', language: 'java', code: `class VendingMachine {
  int balance = 0;
  VendingState state = new IdleState();
  Inventory inventory;
  CoinInventory coins;

  void insertCoin(int cents) { state.insertCoin(this, cents); }
  void selectSlot(String code) { state.selectProduct(this, code); }

  void dispenseProduct(Product p) {
    inventory.decrement(p);
    balance -= p.getPrice();
    coins.dispenseChange(balance);
    balance = 0;
    state = new IdleState();
  }
}` },
      { type: 'callout', tone: 'interview', title: 'Follow-ups', text: 'Card payment adapter, admin restock mode, age-restricted products, audit log of sales.' },
    ],
    references: [
      { label: 'Vending Machine LLD — GeeksforGeeks', url: 'https://www.geeksforgeeks.org/design-a-vending-machine/' },
      { label: 'State pattern — Refactoring Guru', url: 'https://refactoring.guru/design-patterns/state' },
    ],
  },
};
